import type { SVGProps, ReactNode } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function CheckCircleIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </Svg>
  );
}

export function InfinityIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.33-6 4Z" />
    </Svg>
  );
}

export function ApiIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M8 3H7a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h1" />
      <path d="M16 3h1a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-1" />
    </Svg>
  );
}

export function IntegrationIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="m10 9-2 3 2 3" />
      <path d="m14 9 2 3-2 3" />
    </Svg>
  );
}

export function CalendarCheckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18M8 2v4M16 2v4" />
      <path d="m9 15 2 2 4-4" />
    </Svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18M8 2v4M16 2v4" />
    </Svg>
  );
}

export function ManageSearchIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 8h6M3 12h4M3 16h4" />
      <circle cx="15" cy="14" r="5" />
      <path d="m21 20-2.5-2.5" />
    </Svg>
  );
}

export function GlobeSearchIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="8" />
      <path d="M3 11h16M11 3c2.5 2.5 2.5 13.5 0 16M11 3C8.5 5.5 8.5 16.5 11 19" />
      <path d="m21 21-3.5-3.5" />
    </Svg>
  );
}

export function HubIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="4" r="1.5" />
      <circle cx="12" cy="20" r="1.5" />
      <circle cx="4.7" cy="7.8" r="1.5" />
      <circle cx="19.3" cy="7.8" r="1.5" />
      <circle cx="4.7" cy="16.2" r="1.5" />
      <circle cx="19.3" cy="16.2" r="1.5" />
      <path d="M12 9V5.6M12 15v3.4M9.5 10.5 6 8.8M14.5 10.5 18 8.8M9.5 13.5 6 15.2M14.5 13.5 18 15.2" />
    </Svg>
  );
}

export function FactCheckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h5M8 12h3" />
      <path d="m14.5 15.5 1.5 1.5 3-3" />
    </Svg>
  );
}

export function PersonSearchIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M4 19c0-3 2.7-5.5 6-5.5 1.3 0 2.6.4 3.6 1.1" />
      <circle cx="17" cy="16.5" r="3" />
      <path d="m21 20.5-1.8-1.8" />
    </Svg>
  );
}

export function DomainIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 21V6a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v15" />
      <path d="M15 10h4a1 1 0 0 1 1 1v10" />
      <path d="M3 21h18" />
      <path d="M7.5 9h.01M11 9h.01M7.5 13h.01M11 13h.01M7.5 17h.01M11 17h.01" />
    </Svg>
  );
}

export function PropertyIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m3 10 9-7 9 7" />
      <path d="M5 9v11h14V9" />
      <path d="M10 20v-6h4v6" />
    </Svg>
  );
}

export function ShieldCheckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3 5 6v5c0 4.5 3 7.8 7 9 4-1.2 7-4.5 7-9V6Z" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  );
}

export function GavelIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m13 6 5 5" />
      <path d="M9.5 9.5 3 16a2.1 2.1 0 0 0 3 3l6.5-6.5" />
      <path d="m14.5 4.5 5 5" />
      <path d="m11 7 6 6" />
      <path d="M14 21h7" />
    </Svg>
  );
}

export function BrushIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9.5 12 18 3.5a2 2 0 0 1 3 3L12.5 15" />
      <path d="M7 14c-1.7 0-3 1.3-3 3 0 1.3-1 2-2 2 1 1.2 2.5 2 4 2 2.2 0 4-1.8 4-4 0-1.7-1.3-3-3-3Z" />
    </Svg>
  );
}

export function ShareIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="18" cy="5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="19" r="2.5" />
      <path d="m8.5 13.5 7 4M15.5 6.5l-7 4" />
    </Svg>
  );
}

export function CodeIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m16 18 4-4-4-4" />
      <path d="m8 6-4 4 4 4" />
      <path d="m14 4-4 16" />
    </Svg>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </Svg>
  );
}

export function InfoIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </Svg>
  );
}

export function ContractIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5" />
      <path d="M8 16c1-1.2 2-1.2 3 0s2 1.2 3 0" />
    </Svg>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </Svg>
  );
}

export function GlobeIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.6 2.7 2.6 15.3 0 18M12 3C9.4 5.7 9.4 18.3 12 21" />
    </Svg>
  );
}
